import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, Gift } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { cad, cadShort, imgUrl } from "@/lib/api";

const FREE_SHIP = 100;
const WRAP = 5;

export const CartDrawer = () => {
  const { items, remove, setQty, subtotal, drawerOpen, setDrawerOpen, giftWrap, setGiftWrap } =
    useCart();
  const { storeOpen } = useStore();
  const navigate = useNavigate();
  const remaining = Math.max(FREE_SHIP - subtotal, 0);
  const pct = Math.min((subtotal / FREE_SHIP) * 100, 100);

  const go = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="w-full sm:max-w-md bg-page border-l border-soft-border flex flex-col p-0"
      >
        <SheetHeader className="p-6 border-b border-soft-border">
          <SheetTitle className="font-serif text-2xl text-charcoal text-left flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-gold" /> Your Cart
          </SheetTitle>
          <SheetDescription className="sr-only">Items in your shopping cart</SheetDescription>
        </SheetHeader>

        <div className="px-6 py-4 bg-blush border-b border-soft-border">
          {remaining > 0 ? (
            <p className="text-xs text-charcoal/75">
              You're <span className="font-semibold text-gold">{cadShort(remaining)}</span> away
              from free shipping
            </p>
          ) : (
            <p className="text-xs font-semibold text-gold">🎉 You've unlocked free shipping!</p>
          )}
          <div
            data-testid="cart-free-shipping-progress"
            className="mt-2 h-1.5 rounded-full bg-white overflow-hidden"
          >
            <div
              className="h-full bg-gradient-to-r from-blush-accent to-gold transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16">
              <p className="text-charcoal/60 text-sm">Your cart is empty.</p>
              <Button
                data-testid="drawer-shop-btn"
                onClick={() => go("/shop")}
                className="mt-4 bg-charcoal text-white rounded-none hover:bg-charcoal/90"
              >
                Start Shopping
              </Button>
            </div>
          )}
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-3" data-testid={`cart-line-${it.product_id}`}>
              <div className="h-20 w-16 shrink-0 overflow-hidden rounded-sm bg-blush">
                {it.image && <img src={imgUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                  <p className="font-serif text-sm text-charcoal leading-tight">{it.name}</p>
                  <button
                    data-testid={`cart-remove-${it.product_id}`}
                    onClick={() => remove(it.product_id)}
                    className="text-charcoal/40 hover:text-charcoal"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-charcoal/60 mt-0.5">{cad(it.price)}</p>
                <div className="mt-2 inline-flex items-center border border-soft-border rounded-full">
                  <button
                    data-testid={`cart-dec-${it.product_id}`}
                    onClick={() => setQty(it.product_id, it.quantity - 1)}
                    className="h-7 w-7 grid place-items-center text-charcoal/70"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-8 text-center text-sm">{it.quantity}</span>
                  <button
                    data-testid={`cart-inc-${it.product_id}`}
                    onClick={() => setQty(it.product_id, it.quantity + 1)}
                    className="h-7 w-7 grid place-items-center text-charcoal/70"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-soft-border p-6 space-y-4 bg-white">
            <button
              data-testid="gift-wrapping-checkbox"
              onClick={() => setGiftWrap(!giftWrap)}
              className={`w-full flex items-center gap-3 p-3 rounded-sm border text-left transition-colors ${
                giftWrap ? "border-gold bg-gold-light" : "border-soft-border"
              }`}
            >
              <span
                className={`h-5 w-5 rounded-sm border grid place-items-center ${
                  giftWrap ? "bg-gold border-gold" : "border-charcoal/30"
                }`}
              >
                {giftWrap && <Gift className="h-3 w-3 text-white" />}
              </span>
              <span className="text-sm text-charcoal">
                Add gift wrapping <span className="text-charcoal/60">(+{cadShort(WRAP)})</span>
              </span>
            </button>
            <div className="flex justify-between text-sm">
              <span className="text-charcoal/70">Subtotal</span>
              <span className="font-semibold text-charcoal">{cad(subtotal)}</span>
            </div>
            <Button
              data-testid="drawer-checkout-btn"
              onClick={() => go("/checkout")}
              disabled={!storeOpen}
              className="w-full bg-charcoal text-white rounded-none hover:bg-gold h-12 tracking-wide font-display disabled:opacity-50 disabled:hover:bg-charcoal"
            >
              {storeOpen ? "Checkout" : "Opening Soon"}
            </Button>
            <button
              data-testid="drawer-viewcart-btn"
              onClick={() => go("/cart")}
              className="w-full text-xs eyebrow text-charcoal/60 hover:text-gold"
            >
              View Full Cart
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
